import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomPanelTab } from '../../types/bottomPanel';
import TerminalTab from '../../components/BottomPanelTabs/TerminalTab';
import OutputTab from '../../components/BottomPanelTabs/OutputTab';
import ProblemsTab, { Problem } from '../../components/BottomPanelTabs/ProblemsTab';
import DebugConsoleTab, { DebugLog } from '../../components/BottomPanelTabs/DebugConsoleTab';
import { FaTerminal, FaFileAlt, FaExclamationTriangle, FaBug, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { Mode } from '../../types/modes';
import './BottomPanel.css';

interface BottomPanelProps {
  isVisible: boolean;
  onToggleVisibility: () => void;
}

const BottomPanel: React.FC<BottomPanelProps> = ({
  isVisible,
  onToggleVisibility,
}) => {
  const { t } = useTranslation();
  const { mode } = useGlobalContext();
  
  // Join modunda bottom panel'i gösterme
  if (mode === Mode.Join) {
    return null;
  }

  // Tab management
  const [activeTab, setActiveTab] = useState<BottomPanelTab>(BottomPanelTab.TERMINAL);
  
  // Resize management
  const [height, setHeight] = useState<number>(250);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  
  // Output state
  const [outputs, setOutputs] = useState<string[]>([]);
  
  // Problems state
  const [problems, setProblems] = useState<Problem[]>([]);
  
  // Debug console state
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [debugInput, setDebugInput] = useState('');

  // Debug console submit handler
  const handleDebugSubmit = (cmd: string) => {
    setDebugLogs(prev => [...prev, {
      id: Date.now().toString(),
      type: 'log' as const,
      message: `> ${cmd}`,
      timestamp: new Date()
    }]);
  };

  // Resize handler
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = startYRef.current - e.clientY;
      const newHeight = Math.max(120, Math.min(600, startHeightRef.current + deltaY));
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, height]);

  const tabs = [
    {
      id: BottomPanelTab.TERMINAL,
      label: t('bottomPanel.terminal', 'Terminal'),
      icon: <FaTerminal size={13} />,
      count: undefined,
    },
    {
      id: BottomPanelTab.OUTPUT,
      label: t('bottomPanel.output', 'Output'),
      icon: <FaFileAlt size={13} />,
      count: undefined,
    },
    {
      id: BottomPanelTab.PROBLEMS,
      label: t('bottomPanel.problems', 'Problems'),
      icon: <FaExclamationTriangle size={13} />,
      count: problems.length > 0 ? problems.length : undefined,
    },
    {
      id: BottomPanelTab.DEBUG_CONSOLE,
      label: t('bottomPanel.debugConsole', 'Debug Console'),
      icon: <FaBug size={13} />,
      count: undefined,
    },
  ];

  return (
    <div 
      ref={panelRef}
      className={`bottom-panel ${isVisible ? 'visible' : 'collapsed'}`}
      style={{ height: isVisible ? `${height}px` : '35px' }}
    >
      <div 
        className="bottom-panel-resize-handle"
        onMouseDown={handleMouseDown}
        style={{ display: isVisible ? 'block' : 'none' }}
      />
      <div className="bottom-panel-header">
        <div className="bottom-panel-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`bottom-panel-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              style={{ display: isVisible ? 'flex' : 'none' }}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
              {tab.count !== undefined && (
                <span className="tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
        <div className="bottom-panel-actions">
          <button
            className="bottom-panel-action"
            onClick={onToggleVisibility}
            title={isVisible ? t('bottomPanel.hide', 'Hide Panel') : t('bottomPanel.show', 'Show Panel')}
          >
            {isVisible ? <FaChevronDown size={12} /> : <FaChevronUp size={12} />}
          </button>
        </div>
      </div>
      <div className="bottom-panel-content" style={{ display: isVisible ? 'block' : 'none' }}>
        <div className={`bottom-panel-tab-content ${activeTab === BottomPanelTab.TERMINAL ? 'active' : ''}`}>
          <TerminalTab />
        </div>
        <div className={`bottom-panel-tab-content ${activeTab === BottomPanelTab.OUTPUT ? 'active' : ''}`}>
          <OutputTab outputs={outputs} />
        </div>
        <div className={`bottom-panel-tab-content ${activeTab === BottomPanelTab.PROBLEMS ? 'active' : ''}`}>
          <ProblemsTab problems={problems} />
        </div>
        <div className={`bottom-panel-tab-content ${activeTab === BottomPanelTab.DEBUG_CONSOLE ? 'active' : ''}`}>
          <DebugConsoleTab
            logs={debugLogs}
            input={debugInput}
            onInputChange={setDebugInput}
            onInputSubmit={handleDebugSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default BottomPanel; 